import React, { useState, useEffect } from 'react';
import {
    Box, Button, List, ListItem, ListItemText, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Collapse, Typography,
    ListItemAvatar, Avatar, Chip, Divider, Menu, MenuItem, ListItemIcon, Pagination,
    Select,
    FormControl,
    InputLabel,
    Dialog, DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
} from '@mui/material';
import {
    ExpandMore, ExpandLess,
    Edit, Delete,
} from '@mui/icons-material';

import { fetchCompetitionParticipants, competitionParticipantsStatus } from '../../services/Competition';
import { timeAgo } from '../../../utils/TimeFormat';
import { getImageUrlAdminToken } from '../../utills/ImagesUtilsAdmin';
import { useNavigate } from 'react-router-dom';
import DeclarationDialog from './DeclarationDialog';
import CustomSnackbar from "../../CustomSnackbar";


const CompetitionEntries = ({ competitionId }) => {
    const navigate = useNavigate();
    const [expandedRow, setExpandedRow] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [individualRatingsPage, setIndividualRatingsPage] = useState({});
    const [participantsData, setParticipantsData] = useState([]);
    const [pagination, setPagination] = useState({
        participants_rankings_page: 1,
        total_pages: 1,
    });
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState("");
    const [resultAnnouncedDate, setResultAnnouncedDate] = useState(null);

    const formatDateLike = (dateString) => {
        const date = new Date(dateString);

        return date.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        }).replace(",", "");
    };



    useEffect(() => {
        const fetchParticipants = async () => {
            try {
                const params = {
                    participants_rankings_page: pagination.participants_rankings_page,
                };
                if (expandedRow) {
                    params.competition_entry_id = expandedRow;
                    params.individual_ratings_page = individualRatingsPage[expandedRow] || 1;
                }
                const response = await fetchCompetitionParticipants(competitionId, params);
                console.log("Fetched participants data:", response);
                if (response.status) {
                    setParticipantsData(response.entries.data);
                    setPagination((prev) => ({
                        ...prev,
                        total_pages: response.entries.pagination.last_page,
                    }));
                    setResultAnnouncedDate(response.result_declaration_date);
                }
            } catch (error) {
                console.error("Error fetching participants:", error);
            }
        };

        if (competitionId) {
            fetchParticipants();
        }
    }, [competitionId, pagination.participants_rankings_page, expandedRow, individualRatingsPage]);

    const handleToggle = (id) => {
        setExpandedRow((prev) => (prev === id ? null : id));
        if (!individualRatingsPage[id]) {
            setIndividualRatingsPage((prev) => ({
                ...prev,
                [id]: 1,
            }));
        }
    };

    const handleCloseAction = () => {
        setAnchorEl(null);
    };

    const handleParticipantsPageChange = (event, value) => {
        setPagination((prev) => ({
            ...prev,
            participants_rankings_page: value,
        }));
    };

    const handleIndividualRatingsPageChange = (competition_entry_id, page) => {
        setIndividualRatingsPage((prev) => ({
            ...prev,
            [competition_entry_id]: page,
        }));
    };



    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [loadingStatusUpdate, setLoadingStatusUpdate] = useState(false);

    const handleStatusChange = (competition_entry_id, entity_id, value) => {
        setPendingStatusChange({ competition_entry_id, entity_id, participants_status: value });
        setConfirmDialogOpen(true);
    };

    const confirmStatusUpdate = async () => {
        console.log(pendingStatusChange);
        setLoadingStatusUpdate(true);
        competitionParticipantsStatus(competitionId, pendingStatusChange)
            .then((response) => {
                console.log("Competition participants status updated:", response);
                setMessage(response.message || "Status updated successfully!");
                setSeverity("success");
                setConfirmDialogOpen(false);
                setPendingStatusChange(null);
                //optimistically update the status in UI
                setParticipantsData((prevData) =>
                    prevData.map((entry) =>
                        entry.competition_entry_id === pendingStatusChange.competition_entry_id
                            ? { ...entry, status: pendingStatusChange.participants_status }
                            : entry
                    )
                );

                // if status changed to the disqualify, remove from the list
                if (pendingStatusChange.participants_status === "disqualified") {
                    setParticipantsData((prevData) =>
                        prevData.filter((entry) => entry.competition_entry_id !== pendingStatusChange.competition_entry_id)
                    );
                }
                setLoadingStatusUpdate(false);
            })
            .catch((error) => {
                console.error("Error updating competition participants status:", error);
                // Optional: revert state on error
                // setPendingStatusChange(pendingStatusChange.competition_entry_id, pendingStatusChange.entity_id, /* previous status value */);
                setMessage(error.message || "Failed to update status. Please try again.");
                setSeverity("error");
                setLoadingStatusUpdate(false);
            });
    };

    const cancelStatusUpdate = () => {
        setPendingStatusChange(null);
        setConfirmDialogOpen(false);
    };





    return (
        <React.Fragment>
            <Paper elevation={0} sx={{ mt: 2, p: 2, display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                    <Typography variant="h6">
                        {`Total Participants - ${participantsData.length}`}
                    </Typography>
                    <Typography variant="body2">
                        {resultAnnouncedDate ? `Result Announced At - ${formatDateLike(resultAnnouncedDate)}` : null}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={() => setOpenDialog(true)}
                    sx={{ textTransform: 'none' }}
                >
                    Declare Result
                </Button>
            </Paper>

            <Box sx={{ pl: 3, pr: 3 }}>
                <TableContainer component={Paper} variant='outlined'>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table" size="small">
                        <TableHead sx={{ bgcolor: 'grey.500' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Project Title</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Rating</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Project Owner</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {participantsData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        No participants found.
                                    </TableCell>
                                </TableRow>
                            )}
                            {participantsData.map((row) => (
                                <React.Fragment key={row.competition_entry_id}>
                                    <TableRow>
                                        <TableCell>{row.project_title}</TableCell>
                                        <TableCell>{row.average_rating}</TableCell>
                                        <TableCell onClick={() => navigate(`/users/${row.user_id}`)} sx={{ cursor: 'pointer' }}>
                                            <Avatar
                                                alt={row.user_name}
                                                src={getImageUrlAdminToken(row.thumbnail_pic) || "/user-avatar.jpg"}
                                                sx={{ width: 24, height: 24, display: 'inline-block', verticalAlign: 'middle', mr: 1 }}
                                            />
                                            {row.user_name}
                                        </TableCell>
                                        <TableCell>
                                            <FormControl sx={{ minWidth: 145 }} size="small">
                                                <InputLabel id="demo-simple-select-label">Status</InputLabel>
                                                <Select
                                                    value={row.status}
                                                    onChange={(e) => handleStatusChange(row.competition_entry_id, row.entity_id, e.target.value)}
                                                    size="small"
                                                    label="Status"
                                                    labelId="demo-simple-select-label"
                                                    id={`status-select-${row.competition_entry_id}`} // unique ID
                                                >
                                                    <MenuItem value="submitted" disabled>Submitted</MenuItem>
                                                    <MenuItem value="under_review">Under Review</MenuItem>
                                                    <MenuItem value="approved">Approved</MenuItem>
                                                    <MenuItem value="disqualified">Disqualified</MenuItem>
                                                </Select>
                                            </FormControl>

                                            <Button
                                                variant="outlined"
                                                sx={{ textTransform: 'none', ml: 1, mt: { xs: 1, md: 1, lg: 0 } }}
                                                endIcon={expandedRow === row.competition_entry_id ? <ExpandLess /> : <ExpandMore />}
                                                onClick={() => handleToggle(row.competition_entry_id)}
                                            >
                                                Evaluator
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ p: 0, borderBottom: 0 }}>
                                            <Collapse in={expandedRow === row.competition_entry_id} timeout="auto" unmountOnExit>
                                                <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                                                    <List disablePadding>
                                                        {row.individual_ratings.data.map((rating, index) => (
                                                            <React.Fragment key={index}>
                                                                <ListItem
                                                                    alignItems="flex-start"
                                                                    disableGutters
                                                                // secondaryAction={
                                                                //     <IconButton
                                                                //         edge="end"
                                                                //         aria-label="settings"
                                                                //         onClick={(event) => {
                                                                //             setAnchorEl(event.currentTarget);
                                                                //         }}
                                                                //     >
                                                                //         <MoreVert />
                                                                //     </IconButton>
                                                                // }
                                                                >
                                                                    <ListItemAvatar sx={{ cursor: "pointer" }} onClick={() => navigate(`/users/${rating.user_id}`)}>
                                                                        <Avatar
                                                                            alt={rating.user_name}
                                                                            src={getImageUrlAdminToken(rating.thumbnail_pic) || "/user-avatar.jpg"}
                                                                        />
                                                                    </ListItemAvatar>
                                                                    <ListItemText
                                                                        primary={
                                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                                <Typography fontWeight="bold" variant="body1" sx={{ cursor: "pointer" }} onClick={() => navigate(`/users/${rating.user_id}`)}>
                                                                                    {rating.user_name}
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={`${rating.rating} ★`}
                                                                                    size="small"
                                                                                    color="primary"
                                                                                    sx={{ borderRadius: 1, fontWeight: "bold" }}
                                                                                    clickable={false}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                            </Box>
                                                                        }
                                                                        secondary={rating.describes_me}
                                                                    />
                                                                </ListItem>
                                                                <Box
                                                                    sx={{ display: "flex", justifyContent: "space-between" }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        sx={{ whiteSpace: "pre-line" }}
                                                                    >
                                                                        {rating.review || "No review provided"}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ alignSelf: "flex-end" }}
                                                                    >
                                                                        {timeAgo(rating.reviewed_at)}
                                                                    </Typography>
                                                                </Box>
                                                                <Divider variant="fullWidth" component="li" sx={{ my: 2 }} />
                                                            </React.Fragment>
                                                        ))}
                                                    </List>
                                                    {row.individual_ratings.pagination.total > row.individual_ratings.pagination.per_page && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                            <Pagination
                                                                count={row.individual_ratings.pagination.last_page}
                                                                page={individualRatingsPage[row.competition_entry_id] || 1}
                                                                onChange={(event, page) => handleIndividualRatingsPageChange(row.competition_entry_id, page)}
                                                                variant="outlined" shape="rounded" color='primary'
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Menu
                                                    anchorEl={anchorEl}
                                                    id="review-action-menu"
                                                    open={Boolean(anchorEl)}
                                                    onClose={handleCloseAction}
                                                    onClick={handleCloseAction}
                                                    slotProps={{
                                                        paper: {
                                                            elevation: 0,
                                                            sx: {
                                                                overflow: "visible",
                                                                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                                                                "&::before": {
                                                                    content: '""',
                                                                    display: "block",
                                                                    position: "absolute",
                                                                    top: 0,
                                                                    right: 14,
                                                                    width: 10,
                                                                    height: 10,
                                                                    bgcolor: "background.paper",
                                                                    transform: "translateY(-50%) rotate(45deg)",
                                                                    zIndex: 0,
                                                                },
                                                            },
                                                        },
                                                    }}
                                                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                                                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                                                >
                                                    <MenuItem>
                                                        <ListItemIcon>
                                                            <Edit fontSize="small" />
                                                        </ListItemIcon>
                                                        Edit
                                                    </MenuItem>
                                                    <MenuItem>
                                                        <ListItemIcon>
                                                            <Delete fontSize="small" />
                                                        </ListItemIcon>
                                                        Delete
                                                    </MenuItem>
                                                </Menu>
                                            </Collapse>
                                        </TableCell>

                                    </TableRow>
                                </React.Fragment>
                            ))}

                        </TableBody>
                    </Table>
                </TableContainer>
                {pagination.total_pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                            count={pagination.total_pages}
                            page={pagination.participants_rankings_page}
                            onChange={handleParticipantsPageChange}
                            variant="outlined" shape="rounded" color='primary'
                        />
                    </Box>
                )}
            </Box>

            <DeclarationDialog
                open={openDialog}
                setOpenDialog={setOpenDialog}
                competitionId={competitionId}
                setResultAnnouncedDate={setResultAnnouncedDate}
            />

            <CustomSnackbar
                open={!!message}
                message={message}
                severity={severity}
                onClose={() => setMessage("")}
            />

            {/* Status Change Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={cancelStatusUpdate}
                aria-labelledby="confirm-status-dialog"
            >
                <DialogTitle id="confirm-status-dialog">Confirm Status Change</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to change the status to{" "}
                        <strong>
                            {pendingStatusChange ? pendingStatusChange.participants_status.charAt(0).toUpperCase() + pendingStatusChange.participants_status.slice(1).replaceAll('_', ' ') : ''}
                        </strong>
                        ? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelStatusUpdate} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={confirmStatusUpdate} disableElevation variant="contained" color="primary" sx={{ textTransform: 'none' }}
                        disabled={loadingStatusUpdate}
                        startIcon={loadingStatusUpdate ? <CircularProgress size={16} /> : null}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>


        </React.Fragment>
    );
};

export default CompetitionEntries;